import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../Global/Layout';
import DataTable from '../Global/DataTable';
import { showError } from '../../utils/sweetAlertConfig';
import { API_CONFIG, LOTTO_TYPE, ICON_ACTION, dateStampToDateDB, parseDatetimeLocal } from '../../contexts/constants';
import { LottoBallsDisplay } from '../../utils/LottoBalls';
import { formatDateTable } from '../../utils/dateFormatter';
import DatePicker from 'react-datepicker';
import { th } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

const History = ({ onLogout, userRole }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [typeFilter, setTypeFilter] = useState({
    powerball: true,
    megamillions: true,
  });
  const navigate = useNavigate();


  useEffect(() => {
    fetchHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  if (!['sysadmin', 'owner', 'admin', 'agent', 'customer'].includes(userRole)) {
    window.location.href = '/forbidden';
    return null;
  }

  const fetchHistoryData = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', pageSize);

      // Add lottery type filters
      if (typeFilter.powerball) params.append('type_powerball', 'true');
      if (typeFilter.megamillions) params.append('type_megamillions', 'true');

      // Add date range filters (use local date format to avoid timezone issues)
      if (startDate) {
        params.append('startDate', dateStampToDateDB(startDate));
      }
      if (endDate) {
        params.append('endDate', dateStampToDateDB(endDate));
      }

      const response = await axios.get(`${API_CONFIG.BASE_URL}/awards/retroact?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (Array.isArray(response.data.data)) {
        // Transform data to match frontend format
        const transformedData = response.data.data.map(item => {
          // Parse win_ball string (format: "1,2,3,4,5")
          const grayBalls = item.winningNumbers ? item.winningNumbers : [];
          const redBall = item.powerBall ? parseInt(item.powerBall) : 0;

          return {
            id: item.id,
            date: item.drawDate,
            dayOfWeek: parseDatetimeLocal(item.drawDate).toLocaleDateString('th-TH', { weekday: 'long' }),
            type: item.type,
            grayBalls: grayBalls,
            redBall: redBall,
            drawDate: item.drawDate
          };
        });

        setHistoryData(transformedData);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        console.error('Invalid data format');
        setHistoryData([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลได้');
      setHistoryData([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };


  const handleSearch = () => {
    setCurrentPage(1);
    fetchHistoryData();
  };

  const handleClearSearch = () => {
    setStartDate(null);
    setEndDate(null);
    setTypeFilter({
      powerball: true,
      megamillions: true,
    });
    setCurrentPage(1);
	fetchHistoryData();
  };

  const toggleSearchForm = () => {
    setShowSearchForm(!showSearchForm);
  };

  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(currentPage, safeTotalPages);

  const gotoPage = (p) => {
    if (p < 1 || p > safeTotalPages) return;
    setCurrentPage(p);
  };

  const columns = [
    {
      key: 'date',
      header: 'วันที่',
      className: 'text-center',
      render: (row) => (
        <div className="text-center">{formatDateTable(row.date)}</div>
      )
    },
    {
      key: 'dayOfWeek',
      header: 'วัน',
      render: (row) => (
        <div className="flex gap-2 justify-center">{row.dayOfWeek}</div>
      )
    },
    {
      key: 'type',
      header: 'ประเภท',
      render: (row) => (
        <div className="text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 ${
            row.type === 'powerball'
              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
          }`}>
            {LOTTO_TYPE[row.type]}
          </span>
        </div>
      )
    },
    {
      key: 'results',
      header: 'ผลรางวัล',
      className: 'text-center',
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <LottoBallsDisplay grayBalls={row.grayBalls} specialBall={row.redBall} type={row.type} />
        </div>
      )
    },
    {
      key: 'viewDetails',
      header: 'ดูรายละเอียด',
      className: 'text-center',
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => row.type === 'powerball' ? navigate('/results/results-powerball?rpid='+row.id) : navigate('/results/results-megamillions?rpid='+row.id)}
            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
            title="ดูรายละเอียด"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout title="รายการผลรางวัลย้อนหลัง" onLogout={onLogout} userRole={userRole}>
      <div className="mb-4 flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">ผลรางวัลย้อนหลังทั้งหมด</p>
		<button
          onClick={toggleSearchForm}
		  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
            showSearchForm
              ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
              : 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
          }`}
        >
          {showSearchForm ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
          <span className='hidden md:inline'>ค้นหา</span>
        </button>
      </div>

      {/* Search Form - Show/Hide */}
      {showSearchForm && (
        <div className="mb-6 bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-4 animate-fade-in">

          {/* Lottery Type Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              ประเภท
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={typeFilter.powerball}
                  onChange={(e) => setTypeFilter({ ...typeFilter, powerball: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Powerball</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={typeFilter.megamillions}
                  onChange={(e) => setTypeFilter({ ...typeFilter, megamillions: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Mega Millions</span>
              </label>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                วันที่เริ่มต้น
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                locale={th}
                dateFormat="dd/MM/yyyy"
                placeholderText="เลือกวันที่เริ่มต้น"
                isClearable
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                wrapperClassName="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                วันที่สิ้นสุด
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                locale={th}
                dateFormat="dd/MM/yyyy"
                placeholderText="เลือกวันที่สิ้นสุด"
                isClearable
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                wrapperClassName="w-full"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              {ICON_ACTION.SEARCH}
              ค้นหา
            </button>
			<button
				onClick={handleClearSearch}
				className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
			>
				{ICON_ACTION.CLEAR}
				ล้าง
			</button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={historyData}
        loading={loading}
        page={safeCurrentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={gotoPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        emptyMessage="ไม่มีข้อมูลประวัติ"
      />
    </Layout>
  );
};

export default History;
